import { CONTENT_TYPES, type ContentTypeId } from './contentActionModel.ts';

/* ----------------------------------------------------------------------------
 * ContentTypeSelector (P1) — the AI Studio content-type chooser. Six types are
 * shown; only `blog_article` is ACTIVE today (P1), so the other five render as
 * disabled "on the roadmap" chips — the surface is honest about what it can
 * produce now. `blog_article` is preselected and cannot be deselected onto an
 * inactive type (disabled buttons can't be chosen), so the long-form view always
 * has a producible type. Pure/presentational — no data fetching, no side effects.
 * -------------------------------------------------------------------------- */
export function ContentTypeSelector({
  selected,
  onSelect,
}: {
  selected: ContentTypeId;
  onSelect?: (id: ContentTypeId) => void;
}) {
  return (
    <div>
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
        Content type
      </div>
      <div role="group" aria-label="Content type" className="flex flex-wrap gap-1.5">
        {CONTENT_TYPES.map((t) => {
          const isSelected = t.id === selected;
          const chip = t.active
            ? isSelected
              ? 'border-ai bg-ai-wash/70 text-text-primary'
              : 'border-border-default bg-canvas text-text-secondary hover:border-ai hover:bg-ai-wash/40'
            : 'cursor-not-allowed border-border-subtle bg-inset text-text-tertiary';
          return (
            <button
              key={t.id}
              type="button"
              disabled={!t.active}
              aria-pressed={isSelected}
              aria-disabled={!t.active}
              title={t.active ? t.label : t.label + ' — on the roadmap'}
              onClick={() => t.active && onSelect?.(t.id)}
              className={
                'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors duration-fast max-lg:min-h-[44px] ' +
                chip
              }
            >
              {t.label}
              {!t.active && (
                <span className="rounded-sm bg-border-subtle px-1 py-0.5 text-[8px] uppercase tracking-wide text-text-tertiary">
                  Soon
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
